import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import type { SelectedImage } from "../../store";

export const MAX_IMAGES = 10;

const uploadErrorMessages: Record<string, () => string> = {
  "file-invalid-type": () =>
    t("image_space.post_modal.steps.upload_images.errors.file_invalid_type"),
  "file-too-large": () =>
    t("image_space.post_modal.steps.upload_images.errors.file_too_large"),
  "too-many-files": () =>
    t("image_space.post_modal.steps.upload_images.errors.too_many_files", {
      max_images: MAX_IMAGES,
    }),
};

interface UseMultiImageUploadProps {
  handleFileSelection: (files: File[]) => void;
  selectedFiles: SelectedImage[];
  uploadedFilesCount: number;
}

export const useMultiImageUpload = ({
  handleFileSelection,
  selectedFiles,
  uploadedFilesCount,
}: UseMultiImageUploadProps) => {
  const toast = useToast();
  const maxImages = MAX_IMAGES - uploadedFilesCount;

  const handleFileRejection = (fileRejections: FileRejection[]) => {
    const errors = fileRejections.reduce<string[]>((acc, curr) => {
      const error = curr.errors[0].code;
      if (!acc.includes(error)) {
        acc.push(error);
      }
      return acc;
    }, []);

    showUploadErrors(errors);
  };

  const showUploadErrors = (errors: string[]) => {
    errors.forEach(error => {
      const options = { shouldShowCloseButton: true, shouldUseProgress: false };
      // We use "success" for the toast just to match the figma design ...
      const getMessage =
        uploadErrorMessages[error] ??
        (() =>
          t(
            "image_space.post_modal.steps.upload_images.errors.something_went_wrong",
          ));
      return toast.error(getMessage(), options);
    });
  };

  const handleDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => {
    if (fileRejections.length > 0) {
      handleFileRejection(fileRejections);
    } else {
      if (selectedFiles.length + acceptedFiles.length > maxImages) {
        showUploadErrors(["too-many-files"]);
      } else {
        handleFileSelection(acceptedFiles);
      }
    }
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      onDrop: handleDrop,
      noClick: true,
      accept: {
        "image/*": [".jpg", ".jpeg", ".png"],
      },
      maxFiles: maxImages,
      maxSize: 20_000_000,
      multiple: true,
    });

  const isDragActive = isDragAccept || isDragReject;

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    open,
  };
};
